import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { GitBranch } from "lucide-react";
import api from "@/lib/api";

/**
 * AmbivalenceCard — captures the co-existence of opposite-polarity patterns
 * within the same social context. Activated ONLY when both poles are present
 * with N ≥ 3 each (within a context that has ≥10 entries in 30 days).
 *
 * Backend: GET /api/voice-mood/ambivalence/{context}
 *
 * The body text is intentionally reflexive and brief, not narrative — Laura
 * wanted an *invito all'auto-osservazione*, not a clinical explanation.
 */
export const AmbivalenceCard = ({ context, isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/voice-mood/ambivalence/${context}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [context]);

  // The card never renders when ambivalence is not active. It's a
  // clinically-meaningful surfacing, not a default UI element.
  if (loading || !data || !data.active) return null;

  const ctxLabel = data.context_label || context;
  const ctxLabelLower = ctxLabel.toLowerCase();
  const isSolo = context === "solo";

  return (
    <Card className="border-2 border-violet-200/70 bg-violet-50/30" data-testid={`ambivalence-${context}-card`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-violet-700" />
          {isSolo ? "Polarità · Da solo" : `Polarità relazionale · ${ctxLabel}`}
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">
          {isSolo
            ? "Lo stesso \"stare da solo\" produce per te esperienze opposte."
            : `Lo stesso contesto "${ctxLabelLower}" produce per te esperienze opposte.`}
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Le due polarità affiancate */}
        <div className="grid grid-cols-2 gap-2" data-testid={`ambivalence-${context}-poles`}>
          <div className="border border-emerald-200 bg-emerald-50/60 rounded p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1">
              Polo positivo
            </div>
            <div className="text-sm font-semibold text-slate-800 leading-snug">
              {data.positive.name}
            </div>
            {isClinician && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {data.positive.shorthand} · N={data.positive.n} · {data.positive.pct}%
              </div>
            )}
            {!isClinician && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {data.positive.pct}% dei momenti
              </div>
            )}
          </div>

          <div className="border border-rose-200 bg-rose-50/60 rounded p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-rose-800 font-semibold mb-1">
              Polo negativo
            </div>
            <div className="text-sm font-semibold text-slate-800 leading-snug">
              {data.negative.name}
            </div>
            {isClinician && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {data.negative.shorthand} · N={data.negative.n} · {data.negative.pct}%
              </div>
            )}
            {!isClinician && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {data.negative.pct}% dei momenti
              </div>
            )}
          </div>
        </div>

        {/* Testo riflessivo - laura wording */}
        <p
          className="text-xs leading-relaxed text-foreground/90 italic"
          data-testid={`ambivalence-${context}-prompt`}
        >
          Prova a riflettere su quali elementi determinano il passaggio da uno
          stato all'altro.
        </p>

        {isClinician && (
          <p className="text-[10px] text-muted-foreground italic border-t border-dashed border-slate-200 pt-2">
            [Clinico] {data.n_context_total} check-in nel contesto ·
            {" "}{data.n_positive_total} su polo positivo, {data.n_negative_total} su polo negativo ·
            soglia attivazione N≥{data.min_per_pole} per polo.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default AmbivalenceCard;
